import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { TENANT } from '../../config';
import {
  Shield,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Clock,
  DollarSign,
  TrendingDown,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  Ban,
  Play,
  Zap,
} from 'lucide-react';

/* ───────────────────────── types ───────────────────────── */

interface FBOBalance {
  tenant_id: string;
  available_balance_cents: number;
  held_balance_cents: number;
  reserve_balance_cents: number;
  total_balance_cents: number;
  total_inbound_cents: number;
  total_outbound_cents: number;
  total_returns_cents: number;
  last_payout_at: string | null;
}

interface PayoutRequest {
  id: string;
  tenant_id: string;
  amount_cents: number;
  destination_account_id: string | null;
  increase_transfer_id: string | null;
  status: string;
  hold_until: string;
  approved_by: string | null;
  approved_at: string | null;
  initiated_at: string | null;
  settled_at: string | null;
  failure_reason: string | null;
  created_at: string;
}

interface RiskReserve {
  tenant_id: string;
  reserve_pct_bps: number;
  min_reserve_cents: number;
  current_reserve_cents: number;
  target_reserve_cents: number;
  ach_volume_30d_cents: number;
  last_calculated_at: string | null;
}

interface ReturnRate {
  tenant_id: string;
  period_start: string;
  period_end: string;
  total_ach_count: number;
  total_ach_volume_cents: number;
  return_count: number;
  return_volume_cents: number;
  return_rate_pct: number;
  r01_count: number;
  r02_count: number;
  r03_count: number;
  r29_count: number;
  other_return_count: number;
  alert_level: 'green' | 'yellow' | 'red';
}

interface DashboardData {
  totalPooledBalance: number;
  totalAvailable: number;
  totalHeld: number;
  totalReserves: number;
  pendingPayouts: number;
  pendingPayoutAmount: number;
  avgReturnRate: number;
  tenantCount: number;
  balances: FBOBalance[];
  reserves: RiskReserve[];
  returnRates: ReturnRate[];
  payouts: PayoutRequest[];
}

interface LedgerEntry {
  id: string;
  tenant_id: string;
  entry_type: string;
  amount_cents: number;
  running_balance_cents: number;
  source_type: string;
  source_id: string | null;
  ach_transfer_id: string | null;
  notes: string | null;
  created_at: string;
}

/* ───────────────────────── component ───────────────────────── */

const ProgramManagerDashboard: React.FC = () => {
  const isPlatformOwner = TENANT.id === 'redex';
  const [data, setData] = useState<DashboardData | null>(null);
  const [ledger, setLedger] = useState<LedgerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [runningCycle, setRunningCycle] = useState(false);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [releasingPayouts, setReleasingPayouts] = useState(false);
  const [activeSection, setActiveSection] = useState<'overview' | 'payouts' | 'ledger'>('overview');

  const fmtUSD = (cents: number | string) => {
    const c = typeof cents === 'string' ? parseInt(cents, 10) : cents;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((c || 0) / 100);
  };

  const fmtPct = (pct: number | string) => {
    const p = typeof pct === 'string' ? parseFloat(pct) : pct;
    return `${(p || 0).toFixed(2)}%`;
  };

  const fmtDate = (d: string | null) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const loadDashboard = useCallback(async () => {
    try {
      const [dashRes, balAllRes, payRes, resRes, retRes, ledRes] = await Promise.all([
        api.get('/admin/clearing/dashboard').catch(() => null) as Promise<any>,
        api.get('/admin/clearing/balances/all').catch(() => null) as Promise<any>,
        api.get('/admin/clearing/payouts').catch(() => null) as Promise<any>,
        api.get('/admin/clearing/reserves').catch(() => null) as Promise<any>,
        api.get('/admin/clearing/returns/all').catch(() => null) as Promise<any>,
        api.get('/admin/clearing/ledger?limit=50').catch(() => null) as Promise<any>,
      ]);

      const dash = dashRes?.data || {};
      const balances: FBOBalance[] = Array.isArray(balAllRes?.data) ? balAllRes.data : [];
      const payouts: PayoutRequest[] = Array.isArray(payRes?.data) ? payRes.data : [];
      const reserves: RiskReserve[] = Array.isArray(resRes?.data) ? resRes.data : [];
      const returnRates: ReturnRate[] = Array.isArray(retRes?.data) ? retRes.data : [];

      // Aggregate from dashboard response or compute from arrays
      const totalPooled = balances.reduce((s, b) => s + num(b.total_balance_cents), 0);
      const totalAvail = balances.reduce((s, b) => s + num(b.available_balance_cents), 0);
      const totalHeld = balances.reduce((s, b) => s + num(b.held_balance_cents), 0);
      const totalRes = balances.reduce((s, b) => s + num(b.reserve_balance_cents), 0);
      const pendingPay = payouts.filter(p => ['pending', 'held', 'eligible', 'approved'].includes(p.status));

      setData({
        totalPooledBalance: dash.totalPooledBalance || totalPooled,
        totalAvailable: dash.totalAvailable || totalAvail,
        totalHeld: dash.totalHeld || totalHeld,
        totalReserves: dash.totalReserves || totalRes,
        pendingPayouts: dash.pendingPayouts || pendingPay.length,
        pendingPayoutAmount: dash.pendingPayoutAmount || pendingPay.reduce((s, p) => s + num(p.amount_cents), 0),
        avgReturnRate: dash.avgReturnRate || 0,
        tenantCount: dash.tenantCount || balances.length,
        balances,
        reserves,
        returnRates,
        payouts,
      });

      if (ledRes?.data) {
        setLedger(Array.isArray(ledRes.data) ? ledRes.data : []);
      }
    } catch (err) {
      console.error('Clearing dashboard load error', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const num = (v: any) => {
    if (typeof v === 'number') return v;
    if (typeof v === 'string') return parseInt(v, 10) || 0;
    return 0;
  };

  const runClearingCycle = async () => {
    setRunningCycle(true);
    try {
      await api.post('/admin/clearing/cycle');
      toast.success('Clearing cycle completed');
      await loadDashboard();
    } catch (err: any) {
      toast.error(err?.message || 'Clearing cycle failed');
    } finally {
      setRunningCycle(false);
    }
  };

  const approvePayout = async (id: string) => {
    setApprovingId(id);
    try {
      await api.post(`/admin/clearing/payouts/${id}/approve`);
      toast.success('Payout approved');
      await loadDashboard();
    } catch (err: any) {
      toast.error(err?.message || 'Approve failed');
    } finally {
      setApprovingId(null);
    }
  };

  const releasePayouts = async () => {
    setReleasingPayouts(true);
    try {
      const res = await api.post('/admin/clearing/payouts/release') as any;
      toast.success(`Released ${res?.released || 0} payout(s)`);
      await loadDashboard();
    } catch (err: any) {
      toast.error(err?.message || 'Release failed');
    } finally {
      setReleasingPayouts(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Shield className="w-10 h-10 mx-auto mb-3 opacity-40" />
        <p>Unable to load clearing data. Verify the FBO clearing migration has been applied.</p>
      </div>
    );
  }

  const alertColor = (level: string) => {
    switch (level) {
      case 'green': return { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500' };
      case 'yellow': return { bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-500' };
      case 'red': return { bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500' };
      default: return { bg: 'bg-gray-100', text: 'text-gray-800', dot: 'bg-gray-500' };
    }
  };

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      held: 'bg-orange-100 text-orange-800',
      eligible: 'bg-blue-100 text-blue-800',
      approved: 'bg-indigo-100 text-indigo-800',
      initiated: 'bg-purple-100 text-purple-800',
      settled: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800',
    };
    return map[status] || 'bg-gray-100 text-gray-800';
  };

  const entryTypeLabel = (type: string) => {
    const map: Record<string, { label: string; color: string }> = {
      inbound_settled: { label: 'Inbound', color: 'text-green-600' },
      outbound_payout: { label: 'Outbound', color: 'text-red-600' },
      return_reversal: { label: 'Return', color: 'text-orange-600' },
      reserve_hold: { label: 'Reserve Hold', color: 'text-blue-600' },
      reserve_release: { label: 'Reserve Release', color: 'text-indigo-600' },
      fee_deduction: { label: 'Fee', color: 'text-purple-600' },
      adjustment: { label: 'Adjustment', color: 'text-gray-600' },
    };
    return map[type] || { label: type, color: 'text-gray-600' };
  };

  const sections = [
    { key: 'overview' as const, label: 'Overview' },
    { key: 'payouts' as const, label: `Payouts (${data.payouts.length})` },
    { key: 'ledger' as const, label: 'FBO Ledger' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            {isPlatformOwner ? 'Program Manager Compliance Dashboard' : 'FBO Clearing & Settlement'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {isPlatformOwner
              ? 'FBO clearing, T+2 settlement, risk reserves & ACH return monitoring across all tenants'
              : 'Your FBO balance, settlement holds, risk reserve & ACH return monitoring'}
          </p>
        </div>
        <div className="flex gap-2">
          {isPlatformOwner && (
            <button
              onClick={releasePayouts}
              disabled={releasingPayouts}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border border-border hover:bg-muted disabled:opacity-50"
            >
              <Play className="w-3.5 h-3.5" />
              {releasingPayouts ? 'Releasing…' : 'Release Eligible'}
            </button>
          )}
          <button
            onClick={runClearingCycle}
            disabled={runningCycle}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            <Zap className="w-3.5 h-3.5" />
            {runningCycle ? 'Running…' : 'Run Clearing Cycle'}
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs text-muted-foreground font-medium">{isPlatformOwner ? 'Total Pooled (FBO)' : 'Your FBO Balance'}</p>
            <DollarSign className="w-4 h-4 text-primary" />
          </div>
          <p className="text-xl font-bold">{fmtUSD(data.totalPooledBalance)}</p>
          <p className="text-xs text-muted-foreground mt-1">{isPlatformOwner ? `${data.tenantCount} tenant(s)` : TENANT.branding.name}</p>
        </div>

        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs text-muted-foreground font-medium">Available / Held</p>
            <Clock className="w-4 h-4 text-blue-500" />
          </div>
          <p className="text-xl font-bold">{fmtUSD(data.totalAvailable)}</p>
          <p className="text-xs text-orange-600 font-medium mt-1">
            {fmtUSD(data.totalHeld)} held (T+2)
          </p>
        </div>

        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs text-muted-foreground font-medium">Risk Reserves</p>
            <Shield className="w-4 h-4 text-green-500" />
          </div>
          <p className="text-xl font-bold">{fmtUSD(data.totalReserves)}</p>
          <p className="text-xs text-muted-foreground mt-1">1% ACH vol target</p>
        </div>

        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs text-muted-foreground font-medium">Pending Payouts</p>
            <ArrowUpRight className="w-4 h-4 text-orange-500" />
          </div>
          <p className="text-xl font-bold">{data.pendingPayouts}</p>
          <p className="text-xs text-muted-foreground mt-1">{fmtUSD(data.pendingPayoutAmount)} total</p>
        </div>
      </div>

      {/* Sub-sections */}
      <div className="flex gap-4 border-b border-border">
        {sections.map(s => (
          <button
            key={s.key}
            onClick={() => setActiveSection(s.key)}
            className={`pb-2 text-sm font-medium border-b-2 transition-all ${
              activeSection === s.key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* ──── OVERVIEW SECTION ──── */}
      {activeSection === 'overview' && (
        <div className="space-y-6">
          {/* Per-Tenant FBO Balances */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-3 bg-muted/50 border-b border-border">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <DollarSign className="w-4 h-4" /> FBO Balances by Tenant
              </h3>
            </div>
            {data.balances.length === 0 ? (
              <div className="p-5 text-center text-sm text-muted-foreground">No tenant balances found.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-muted-foreground border-b border-border">
                      <th className="text-left p-3 font-medium">Tenant</th>
                      <th className="text-right p-3 font-medium">Available</th>
                      <th className="text-right p-3 font-medium">Held (T+2)</th>
                      <th className="text-right p-3 font-medium">Reserve</th>
                      <th className="text-right p-3 font-medium">Total</th>
                      <th className="text-right p-3 font-medium">Lifetime In</th>
                      <th className="text-right p-3 font-medium">Lifetime Out</th>
                      <th className="text-right p-3 font-medium">Returns</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.balances.map(b => (
                      <tr key={b.tenant_id} className="border-b border-border hover:bg-muted/30">
                        <td className="p-3 font-medium">{b.tenant_id}</td>
                        <td className="p-3 text-right font-medium">{fmtUSD(b.available_balance_cents)}</td>
                        <td className="p-3 text-right text-orange-600">{fmtUSD(b.held_balance_cents)}</td>
                        <td className="p-3 text-right text-blue-600">{fmtUSD(b.reserve_balance_cents)}</td>
                        <td className="p-3 text-right font-bold">{fmtUSD(b.total_balance_cents)}</td>
                        <td className="p-3 text-right text-green-600">
                          <span className="inline-flex items-center gap-1">
                            <ArrowDownRight className="w-3 h-3" />
                            {fmtUSD(b.total_inbound_cents)}
                          </span>
                        </td>
                        <td className="p-3 text-right text-red-600">
                          <span className="inline-flex items-center gap-1">
                            <ArrowUpRight className="w-3 h-3" />
                            {fmtUSD(b.total_outbound_cents)}
                          </span>
                        </td>
                        <td className="p-3 text-right text-orange-600">{fmtUSD(b.total_returns_cents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Risk Reserves */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-3 bg-muted/50 border-b border-border">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <Shield className="w-4 h-4" /> Per-Tenant Risk Reserves
              </h3>
            </div>
            {data.reserves.length === 0 ? (
              <div className="p-5 text-center text-sm text-muted-foreground">No reserve data.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-muted-foreground border-b border-border">
                      <th className="text-left p-3 font-medium">Tenant</th>
                      <th className="text-right p-3 font-medium">Reserve %</th>
                      <th className="text-right p-3 font-medium">Current</th>
                      <th className="text-right p-3 font-medium">Target</th>
                      <th className="text-right p-3 font-medium">30d ACH Vol</th>
                      <th className="text-center p-3 font-medium">Status</th>
                      <th className="text-right p-3 font-medium">Last Calc</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.reserves.map(r => {
                      const current = num(r.current_reserve_cents);
                      const target = num(r.target_reserve_cents);
                      const pctFilled = target > 0 ? Math.min(100, Math.round((current / target) * 100)) : 100;
                      const isDeficit = current < target;

                      return (
                        <tr key={r.tenant_id} className="border-b border-border hover:bg-muted/30">
                          <td className="p-3 font-medium">{r.tenant_id}</td>
                          <td className="p-3 text-right">{(num(r.reserve_pct_bps) / 100).toFixed(2)}%</td>
                          <td className="p-3 text-right font-medium">{fmtUSD(current)}</td>
                          <td className="p-3 text-right text-muted-foreground">{fmtUSD(target)}</td>
                          <td className="p-3 text-right">{fmtUSD(r.ach_volume_30d_cents)}</td>
                          <td className="p-3 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-20 bg-gray-200 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full ${isDeficit ? 'bg-yellow-500' : 'bg-green-500'}`}
                                  style={{ width: `${pctFilled}%` }}
                                />
                              </div>
                              <span className="text-xs font-medium">{pctFilled}%</span>
                            </div>
                          </td>
                          <td className="p-3 text-right text-xs text-muted-foreground">
                            {fmtDate(r.last_calculated_at)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ACH Return Rate Health */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-3 bg-muted/50 border-b border-border">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <Activity className="w-4 h-4" /> ACH Return Rate Monitoring
              </h3>
            </div>
            {data.returnRates.length === 0 ? (
              <div className="p-5 text-center text-sm text-muted-foreground">
                No return rate data yet. Run a clearing cycle to calculate.
              </div>
            ) : (
              <div className="space-y-4 p-5">
                {data.returnRates.map((rr, idx) => {
                  const colors = alertColor(rr.alert_level);
                  return (
                    <div key={`${rr.tenant_id}-${idx}`} className="border border-border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <span className="font-medium">{rr.tenant_id}</span>
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${colors.bg} ${colors.text}`}>
                            <span className={`w-2 h-2 rounded-full ${colors.dot}`} />
                            {rr.alert_level.toUpperCase()}
                          </span>
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {new Date(rr.period_start).toLocaleDateString()} – {new Date(rr.period_end).toLocaleDateString()}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                        <div>
                          <p className="text-xs text-muted-foreground">Return Rate</p>
                          <p className={`text-lg font-bold ${
                            rr.alert_level === 'red' ? 'text-red-600' :
                            rr.alert_level === 'yellow' ? 'text-yellow-600' : 'text-green-600'
                          }`}>
                            {fmtPct(rr.return_rate_pct)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">ACH Volume</p>
                          <p className="text-sm font-medium">{rr.total_ach_count} txns</p>
                          <p className="text-xs text-muted-foreground">{fmtUSD(rr.total_ach_volume_cents)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Returns</p>
                          <p className="text-sm font-medium text-orange-600">{rr.return_count} returned</p>
                          <p className="text-xs text-muted-foreground">{fmtUSD(rr.return_volume_cents)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">R-Code Breakdown</p>
                          <div className="text-xs space-y-0.5 mt-1">
                            {num(rr.r01_count) > 0 && <p>R01 (Insuf. Funds): <span className="font-medium">{rr.r01_count}</span></p>}
                            {num(rr.r02_count) > 0 && <p>R02 (Closed Acct): <span className="font-medium">{rr.r02_count}</span></p>}
                            {num(rr.r03_count) > 0 && <p>R03 (No Acct): <span className="font-medium">{rr.r03_count}</span></p>}
                            {num(rr.r29_count) > 0 && <p>R29 (Unauthorized): <span className="font-medium">{rr.r29_count}</span></p>}
                            {num(rr.other_return_count) > 0 && <p>Other: <span className="font-medium">{rr.other_return_count}</span></p>}
                            {num(rr.r01_count) + num(rr.r02_count) + num(rr.r03_count) + num(rr.r29_count) + num(rr.other_return_count) === 0 && (
                              <p className="text-green-600">No returns</p>
                            )}
                          </div>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Thresholds</p>
                          <div className="text-xs space-y-0.5 mt-1">
                            <p><span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1" /> &lt; 0.50%</p>
                            <p><span className="inline-block w-2 h-2 rounded-full bg-yellow-500 mr-1" /> 0.50 – 0.99%</p>
                            <p><span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-1" /> ≥ 1.00%</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ──── PAYOUTS SECTION ──── */}
      {activeSection === 'payouts' && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-3 bg-muted/50 border-b border-border flex items-center justify-between">
            <h3 className="font-semibold text-sm flex items-center gap-2">
              <Clock className="w-4 h-4" /> T+2 Payout Queue
            </h3>
            <p className="text-xs text-muted-foreground">
              Payouts are held for 2 business days before release
            </p>
          </div>
          {data.payouts.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              <Clock className="w-8 h-8 mx-auto mb-2 opacity-40" />
              No payout requests yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground border-b border-border">
                    <th className="text-left p-3 font-medium">Tenant</th>
                    <th className="text-right p-3 font-medium">Amount</th>
                    <th className="text-center p-3 font-medium">Status</th>
                    <th className="text-right p-3 font-medium">Hold Until</th>
                    <th className="text-right p-3 font-medium">Created</th>
                    <th className="text-right p-3 font-medium">Settled</th>
                    <th className="text-center p-3 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.payouts.map(p => (
                    <tr key={p.id} className="border-b border-border hover:bg-muted/30">
                      <td className="p-3 font-medium">{p.tenant_id}</td>
                      <td className="p-3 text-right font-medium">{fmtUSD(p.amount_cents)}</td>
                      <td className="p-3 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(p.status)}`}>
                          {p.status}
                        </span>
                      </td>
                      <td className="p-3 text-right text-xs">{fmtDate(p.hold_until)}</td>
                      <td className="p-3 text-right text-xs text-muted-foreground">{fmtDate(p.created_at)}</td>
                      <td className="p-3 text-right text-xs">
                        {p.settled_at ? fmtDate(p.settled_at) : p.failure_reason ? (
                          <span className="text-red-600">{p.failure_reason}</span>
                        ) : '—'}
                      </td>
                      <td className="p-3 text-center">
                        {p.status === 'eligible' && (
                          <button
                            onClick={() => approvePayout(p.id)}
                            disabled={approvingId === p.id}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800 hover:bg-green-200 disabled:opacity-50"
                          >
                            <CheckCircle2 className="w-3 h-3" />
                            {approvingId === p.id ? '…' : 'Approve'}
                          </button>
                        )}
                        {p.status === 'held' && (
                          <span className="text-xs text-orange-600 flex items-center justify-center gap-1">
                            <Clock className="w-3 h-3" /> Holding
                          </span>
                        )}
                        {['settled', 'failed', 'cancelled'].includes(p.status) && (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ──── LEDGER SECTION ──── */}
      {activeSection === 'ledger' && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-3 bg-muted/50 border-b border-border">
            <h3 className="font-semibold text-sm flex items-center gap-2">
              <TrendingDown className="w-4 h-4" /> FBO Sub-Ledger (Recent Entries)
            </h3>
          </div>
          {ledger.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              <DollarSign className="w-8 h-8 mx-auto mb-2 opacity-40" />
              No ledger entries yet. Entries are created when investments or payouts are processed.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground border-b border-border">
                    <th className="text-left p-3 font-medium">Time</th>
                    <th className="text-left p-3 font-medium">Tenant</th>
                    <th className="text-left p-3 font-medium">Type</th>
                    <th className="text-right p-3 font-medium">Amount</th>
                    <th className="text-right p-3 font-medium">Running Bal</th>
                    <th className="text-left p-3 font-medium">Source</th>
                    <th className="text-left p-3 font-medium">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.map(e => {
                    const et = entryTypeLabel(e.entry_type);
                    const amt = num(e.amount_cents);
                    return (
                      <tr key={e.id} className="border-b border-border hover:bg-muted/30">
                        <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">{fmtDate(e.created_at)}</td>
                        <td className="p-3 font-medium">{e.tenant_id}</td>
                        <td className="p-3">
                          <span className={`text-xs font-semibold ${et.color}`}>{et.label}</span>
                        </td>
                        <td className={`p-3 text-right font-medium ${amt >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {amt >= 0 ? '+' : ''}{fmtUSD(amt)}
                        </td>
                        <td className="p-3 text-right font-medium">{fmtUSD(e.running_balance_cents)}</td>
                        <td className="p-3 text-xs text-muted-foreground">
                          {e.source_type}{e.source_id ? ` #${e.source_id.slice(0, 8)}` : ''}
                        </td>
                        <td className="p-3 text-xs text-muted-foreground max-w-[200px] truncate">
                          {e.notes || '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProgramManagerDashboard;
